import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { icons } from '../components/ui/Icons';
import { useAuth } from '../context/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    const err = searchParams.get('error');
    if (err) {
      setError(getErrorMessage(err));
      setShowError(true);
    }

    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
      return;
    }
  }, [isAuthenticated, navigate, searchParams]);

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'discord_oauth_error':
        return 'Fehler bei der Discord-Authentifizierung. Bitte versuchen Sie es erneut.';
      case 'invalid_state':
        return 'Ungültige Sitzung. Bitte versuchen Sie es erneut.';
      case 'user_inactive':
        return 'Ihr Account wurde deaktiviert. Wenden Sie sich an einen Administrator.';
      case 'callback_failed':
        return 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      default:
        return 'Ein unbekannter Fehler ist aufgetreten.';
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-10 dark:bg-neutral-950">
      <Card className="w-full max-w-md border-t-4 border-t-primary-600 shadow-card dark:border-t-primary-500">
        <CardHeader className="text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-md bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
            <icons.Cross className="h-7 w-7" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl">Klinikum Ravenbruch</CardTitle>
          <CardDescription className="mx-auto max-w-sm">
            Internes Intranet für Klinikpersonal. Melden Sie sich mit Ihrem Discord-Account an.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {showError && error && (
            <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-200" role="alert">
              <div className="flex items-start gap-2.5">
                <icons.AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <p>{error}</p>
              </div>
            </div>
          )}

          <Button fullWidth size="lg" variant="primary" onClick={login} disabled={isLoading} leftIcon="MessageSquare">
            {isLoading ? 'Weiterleitung läuft...' : 'Mit Discord anmelden'}
          </Button>

          <div className="rounded-md border border-dashed border-neutral-300 px-3 py-3 text-center text-xs leading-5 text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
            Der Login verwendet die bestehende OAuth2- und Session-Verarbeitung.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
